import DOMPurify from "dompurify";

export type ClipFootnoteParts = {
  /** HTML merged with clip body (caller adds leading <hr/>) */
  mainInnerHtml: string;
  /** HTML to append at document end, or null */
  trailingFooterHtml: string | null;
  didLink: boolean;
  refNum: number | null;
};

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeHtmlAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeHtmlText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Max numeric footnote index from refs [^n] and definitions [^n]: */
export function getMaxFootnoteIndex(doc: string): number {
  let max = 0;
  const patterns = [/\[\^(\d+)\]:/g, /\[\^(\d+)\](?!:)/g];
  for (const re of patterns) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(doc)) !== null) {
      const n = parseInt(m[1], 10);
      if (!Number.isNaN(n)) max = Math.max(max, n);
    }
  }
  return max;
}

export function findExistingFootnoteIndexForUrl(doc: string, url: string): number | null {
  const esc = escapeRegex(url);
  const re = new RegExp(`\\[\\^(\\d+)\\]:\\s*\\[[^\\]]*\\]\\(${esc}\\)`);
  const m = doc.match(re);
  if (!m) return null;
  return parseInt(m[1], 10);
}

function findExistingFootnoteIndexFromHtmlHref(doc: string, url: string): number | null {
  const variants = [escapeHtmlAttr(url), url, url.replace(/&/g, "&amp;")];
  for (const h of variants) {
    const re = new RegExp(`\\[\\^(\\d+)\\]:\\s*<a[^>]*href="${escapeRegex(h)}"`, "i");
    const m = doc.match(re);
    if (m) return parseInt(m[1], 10);
  }
  return null;
}

export function findExistingFootnoteIndexForUrlLoose(doc: string, url: string): number | null {
  const md = findExistingFootnoteIndexForUrl(doc, url);
  if (md != null) return md;
  const encoded = url.replace(/&/g, "&amp;");
  const mdEnc = findExistingFootnoteIndexForUrl(doc, encoded);
  if (mdEnc != null) return mdEnc;
  return findExistingFootnoteIndexFromHtmlHref(doc, url);
}

/** True if document already has a Sources section heading */
export function docHasSourcesHeading(doc: string): boolean {
  return />\s*Sources\s*</i.test(doc) || /##\s*Sources\b/i.test(doc);
}

const purifyFootnote: DOMPurify.Config = {
  ALLOWED_TAGS: ["h2", "p", "a", "br", "strong", "em"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

/**
 * Builds HTML fragments for a clip when an external source URL is known.
 * `clipInnerHtml` is already sanitized clip HTML (caller adds <hr/>).
 */
export function buildClipFootnoteParts(args: {
  documentHtml: string;
  clipInnerHtml: string;
  sourceUrl: string;
  sourceLabel: string;
  /** When set, treat this footnote index as already defined for the URL (e.g. Legend session cache). */
  existingRefOverride?: number | null;
}): ClipFootnoteParts {
  const { documentHtml, clipInnerHtml, sourceUrl, sourceLabel, existingRefOverride } = args;

  const existing =
    existingRefOverride != null && existingRefOverride > 0
      ? existingRefOverride
      : findExistingFootnoteIndexForUrlLoose(documentHtml, sourceUrl);
  const refNum = existing ?? getMaxFootnoteIndex(documentHtml) + 1;

  const refHtml = `<p>[^${refNum}]</p>`;
  const mainInnerHtml = `${clipInnerHtml}${refHtml}`;

  if (existing != null) {
    return {
      mainInnerHtml,
      trailingFooterHtml: null,
      didLink: true,
      refNum,
    };
  }

  const hasSources = docHasSourcesHeading(documentHtml);
  const href = escapeHtmlAttr(sourceUrl);
  const label = escapeHtmlText(sourceLabel);
  const defHtml = `<p>[^${refNum}]: <a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a></p>`;
  const heading = hasSources ? "" : `<h2>Sources</h2>`;
  const trailingFooterHtml = DOMPurify.sanitize(`${heading}${defHtml}`, purifyFootnote);

  return {
    mainInnerHtml,
    trailingFooterHtml,
    didLink: true,
    refNum,
  };
}
